/**
 * Created by Jerker Nord on 2024-03-05.
 */
import {LonerDuckSymbol} from "./LonerDuckSymbol";
import {Loner} from "../../../../Loner";
import {gsap} from "gsap";
import {LonerGameAssets} from "../../../../LonerGameAssets";
import {LonerSpine} from "../../../../components/LonerSpine";
import {SpineUtils} from "../../../../utils/SpineUtils";
import {LonerDuckAnimator} from "../LonerDuckAnimator";

enum MX2_DUCK_ANIMATION_NAMES {
    left = "MX2_smoke_left",
    right = "MX2_smoke_right"
}

enum MX2_DUCK_SKIN_NAMES {
    left = "MX2_left",
    right = "MX2_right",
    left_burn = "MX2_left_burn",
    right_burn = "MX2_right_burn"
}

export class LonerDuckMX2Symbol extends LonerDuckSymbol {

    private static SHOT_IMAGE:string = "duck_syms/MX2/X4";

    private _attentionTL:gsap.core.Timeline;
    private _multiplierUpdateTL:gsap.core.Timeline;
    private _mx2Spine:LonerSpine;

    constructor() {
        super();
        this.MULTIPLIER_TEXT_LEFT_POSITION = new PIXI.Point(-20, -50);
        this.MULTIPLIER_TEXT_RIGHT_POSITION = new PIXI.Point(20, -50);

        this._attentionTL = gsap.timeline({repeat : -1, paused : true});
        this._attentionTL.add(() => {
            this._multiplierText.visible = false
        });
        this._attentionTL.add(gsap.timeline({delay : 0.1}));
        this._attentionTL.add(() => {
            this._multiplierText.visible = true
        });
        this._attentionTL.add(gsap.timeline({delay : 0.1}));
    }

    protected init():void {
        super.init();
        this._mx2Spine = new LonerSpine(Loner.assetLoader.getSpineAsset(LonerGameAssets.MX2_DUCK_SPINE));
        this._mx2Spine.visible = false;
        this.addChild(this._mx2Spine);

        this._explosionImage.scale.set(3, 3);
        this.addChild(this._explosionImage);

        this._symbolSprite.visible = false;
    }

    public startSymbolSpine(textureName:string):void {
        this._mx2Spine.visible = true;
        if(textureName.indexOf("left") > -1) {
            this._mx2Spine.skeleton.setSkinByName(MX2_DUCK_SKIN_NAMES.left);
            SpineUtils.resetSpineAnimation(this._mx2Spine);
            this._mx2Spine.state.setAnimation(0, MX2_DUCK_ANIMATION_NAMES.left, true);
        } else {
            this._mx2Spine.skeleton.setSkinByName(MX2_DUCK_SKIN_NAMES.right);
            SpineUtils.resetSpineAnimation(this._mx2Spine);
            this._mx2Spine.state.setAnimation(0, MX2_DUCK_ANIMATION_NAMES.right, true);
        }
    }

    protected createMultiplierText():void {
        this._multiplierText = new PIXI.BitmapText("x1", {
            fontName : LonerGameAssets.BITMAP_FONT_SSD_MULTIPLIER_GOLD,
            fontSize : 40
        });
        this._multiplierText.anchor = new PIXI.Point(0.5, 0.5);
        this._multiplierText.visible = false;
        this.addChild(this._multiplierText);
    }

    public getSymbolShotTL():gsap.core.Timeline {
        const symbolShotTL:gsap.core.Timeline = gsap.timeline();
        symbolShotTL.add(() => {
            this._explosionImage.visible = true;
            this.isShot = true;
            this._multiplierValue = 0;
        });
        symbolShotTL.add(gsap.timeline({delay : 0.2}));
        symbolShotTL.add(() => {
            this.setSymbol(LonerDuckMX2Symbol.SHOT_IMAGE + this.direction)
        });
        symbolShotTL.add(() => {
            this._explosionImage.visible = false;
            this._mx2Spine.visible = false;
        });
        symbolShotTL.add(() => {
            this._multiplierText.visible = false
        });

        return symbolShotTL;
    }

    public getSymbolBurntTL():gsap.core.Timeline {
        const burntTL:gsap.core.Timeline = gsap.timeline();
        if(this.isShot == true) {
            return burntTL;
        }

        burntTL.add(() => {
            if(this.direction == LonerDuckAnimator.DIRECTION_LEFT) {
                this._mx2Spine.skeleton.setSkinByName(MX2_DUCK_SKIN_NAMES.left_burn);
            } else {
                this._mx2Spine.skeleton.setSkinByName(MX2_DUCK_SKIN_NAMES.right_burn);
            }
            SpineUtils.resetSpineAnimation(this._mx2Spine);
            if(this.direction == LonerDuckAnimator.DIRECTION_LEFT) {
                this._mx2Spine.state.setAnimation(0, MX2_DUCK_ANIMATION_NAMES.left, true);
            } else {
                this._mx2Spine.state.setAnimation(0, MX2_DUCK_ANIMATION_NAMES.right, true);
            }
        });

        return burntTL;
    }

    public startAttentionAnimation():void {
        this._attentionTL.restart();
    }

    public stopAttentionAnimation():void {
        this._attentionTL.pause();
        this._symbolSprite.visible = true;
    }

    private getMultiplierUpdateTL(newMulti:number):gsap.core.Timeline {
        if(this._multiplierUpdateTL) {
            this._multiplierUpdateTL.kill();
        }
        this._multiplierUpdateTL = gsap.timeline();
        this._multiplierUpdateTL.add(gsap.to(this._multiplierText.scale, {
            duration : 0.2,
            x : this._multiplierText.scale.x + 0.1,
            y : this._multiplierText.scale.y + 0.1
        }));
        this._multiplierUpdateTL.add(() => {
            this.setMultiplier(newMulti)
        });

        return this._multiplierUpdateTL;
    }

    public updateSymbolValue():gsap.core.Timeline {
        if(this.isShot == false) {
            if(this.row == 1 && this.reel < 4) {
                //Middle ducks
                return this.getMultiplierUpdateTL(this.multiplierValue);
            }

            if((this.row == 0 || this.row == 2) && this.reel > 0) {
                //Top and bottom ducks
                return this.getMultiplierUpdateTL(this.multiplierValue);
            }
        }
        throw new Error("MX 2 update error");
    }

    public movingDuck():void {
        if(this.row == 0 || this.row == 2) {
            this.reel--;
        } else {
            this.reel++;
        }
    }

    public reset():void {
        this._mx2Spine.visible = false;
        this._multiplierText.scale.set(1, 1);
        this._symbolSprite.visible = false;
        super.reset();
    }
}